import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { isNeverLoggedIn } from "@/components/DriverView/columns/handsFreeAvailability";
import { dedupeDriversByMembership } from "@/hooks/driver/dedupeDriversByMembership";
import { membershipIdsWithFreshHeartbeat, newlySignaledIds } from "@/hooks/driver/unactivatedHeartbeats";
import { useDriverTags } from "@/hooks/driver/useDriverTags";
import { usePersonalDeviceMembers } from "@/hooks/driver/usePersonalDeviceMembers";
import { UNACTIVATED_DRIVER_ROLES, useUnactivatedMembers } from "@/hooks/driver/useUnactivatedMembers";
import { useMinuteClock } from "@/hooks/useMinuteClock";
import {
  computeDriverRoster,
  computeDriverVolatile,
  type DriverVolatileRow,
  driverRosterCollections,
  driverVolatileCollections,
} from "@/models/repositories/Driver";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { transcriptionForContent } from "@/store/feedSelectors";
import { store } from "@/store/store";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import { useKeyedCompute } from "@/store/useKeyedCompute";
import type { DriverFilterConfig, DriverRow } from "@/types/driver";
import { type AcknowledgementRequirement, DEFAULT_REQUIREMENT } from "./acknowledgements";
import {
  activityByDriver,
  type DriverEvent,
  driverEventCollections,
  driverEventFeedsAffected,
  newestDriverEvent,
} from "./driverActivity";
import { useStableRows } from "./stableRows";
import { NO_TASKS_ELSEWHERE, type TasksElsewhere } from "./tasks";
import { useCurrentRoutes } from "./useCurrentRoutes";
import { useMessageContentDownload } from "./useMessageContentDownload";

/** Stable fallback while a compute's first run is pending; a literal `?? []` would change identity every render. */
const NO_ROWS: never[] = [];

/** Every driver the member can see: no tab, team, status, volume, group, or search restriction. */
const PERMISSIVE_FILTER: Omit<DriverFilterConfig, "language"> = {
  tab: "all",
  team: "",
  teamAliases: [],
  sort: "alphaAsc",
  statusFilter: [],
  volumeFilter: [],
  dutyStatusFilter: [],
  groupIds: [],
  aliasNames: [],
  location: null,
  search: "",
  searchTargets: [],
  unactivatedMembershipIds: [],
  personalDeviceMembershipIds: [],
};

/** Keys a Dispatch row by its driver's membership. */
const byMembership = (row: DriverRow) => row.workspaceMembershipId;

/** The roster never consults availability under the permissive filter, so heartbeats re-run only the volatile compute. */
const ROSTER_COLLECTIONS = driverRosterCollections.filter((name) => name !== "wsMembershipAvailability");

/**
 * One row per driver the member can see, for the Dispatch page.
 *
 * Runs the driver view's roster and volatile computes through `useCollectionsCompute` with a permissive filter,
 * merges the volatile columns onto the roster rows the way the driver view does, and attaches tag values, the
 * personal-device flag, the never-logged-in flag (`isNeverLoggedIn`), and the driver's current route (`null` on no
 * route). With no team, activity (unread, latest message) folds across every alias feed a driver has;
 * with a team, each row reads from that team's channel alone, and `feedId` is that channel, so a reply or History
 * opens it. The latest item and its message are the newer of the driver's latest own message and the driver's newest
 * TMS event `requires` takes, as the message-grain rows list it. Filtering and sorting are the view definition's job
 * downstream. A driver whose merged row comes out equal to last time keeps last time's object, so the table skips
 * their row.
 *
 * @param workspaceId - The workspace whose drivers are listed.
 * @param membershipId - The viewing member; nothing is listed until it is known.
 * @param teamAliases - The command aliases keyed to the member's team, as `useTeamAliases` gives them; empty for all.
 * @param requires - What the view asks its team to acknowledge; only its TMS events and tasks pinned to other boards
 *   reach the rows, since the driver's messages already do.
 * @param elsewhere - The open tasks pinned to other boards, by driver; a newer one than the driver's latest message
 *   is the row's most recent thing when `requires` takes them.
 * @returns `rows` in roster order and `total`, the count of drivers the member can see. Under the permissive filter
 *   the roster is that set, so `total` is its length rather than a third compute.
 */
export function useDispatchRows(
  workspaceId: string,
  membershipId: string | undefined,
  teamAliases: readonly string[] = [],
  requires: AcknowledgementRequirement = DEFAULT_REQUIREMENT,
  elsewhere: TasksElsewhere = NO_TASKS_ELSEWHERE,
): { rows: DriverRow[]; total: number } {
  const { myAccount } = useContext(MyAccountContext);
  const language = myAccount?.preferredLanguage ?? "en";
  const { tagValueMap } = useDriverTags();
  const { personalDeviceMembershipIds } = usePersonalDeviceMembers();
  const { unactivatedMembershipIds, refetch: refetchUnactivatedMembers } =
    useUnactivatedMembers(UNACTIVATED_DRIVER_ROLES);
  const routes = useCurrentRoutes(workspaceId);

  const teamAliasesKey = teamAliases.join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: the joined key is the aliases' identity
  const filter = useMemo<DriverFilterConfig>(
    () => ({ ...PERMISSIVE_FILTER, language, teamAliases: [...teamAliases] }),
    [language, teamAliasesKey],
  );

  const rosterRows = useCollectionsCompute(
    ROSTER_COLLECTIONS,
    () =>
      workspaceId && membershipId
        ? computeDriverRoster({ workspaceId, membershipId, myCurrentWorkspaceMembershipId: membershipId, filter })
        : null,
    [workspaceId, membershipId, filter],
    "Dispatch:driverRoster",
  );
  const volatileRows = useCollectionsCompute(
    driverVolatileCollections,
    () => (workspaceId && membershipId ? computeDriverVolatile({ workspaceId, membershipId, language }) : null),
    [workspaceId, membershipId, language],
    "Dispatch:driverVolatile",
  );
  const roster = rosterRows ?? NO_ROWS;
  const volatile = volatileRows ?? NO_ROWS;

  // A fresh heartbeat from a member the never-logged-in set still holds means they just logged in; refetch the set
  // once per member rather than wait out its poll, as the driver view does.
  const signaledActivations = useRef(new Set<string>());
  useEffect(() => {
    signaledActivations.current = new Set();
  }, [workspaceId]);
  useEffect(() => {
    const fresh = membershipIdsWithFreshHeartbeat(volatile, unactivatedMembershipIds);
    const newIds = newlySignaledIds(fresh, signaledActivations.current);
    if (newIds.length === 0) return;
    for (const id of newIds) signaledActivations.current.add(id);
    refetchUnactivatedMembers();
  }, [volatile, unactivatedMembershipIds, refetchUnactivatedMembers]);

  // The roster's feeds are the ones the member may read, so each is asked for its newest event; a TMS event write
  // re-asks only its driver's feeds, and the minute clock ages events out of the lookback.
  const now = useMinuteClock();
  const rosterFeedIds = useMemo(
    () => [...new Set(roster.flatMap((row) => (row.feedId != null ? [row.feedId] : [])))],
    [roster],
  );
  const eventOn = useCallback(
    (feedId: string) => newestDriverEvent(feedId, requires, now, elsewhere),
    [requires, now, elsewhere],
  );
  const eventsByFeed = useKeyedCompute(
    "Dispatch:driverEvents",
    driverEventCollections,
    rosterFeedIds,
    eventOn,
    driverEventFeedsAffected,
    [requires, now, elsewhere],
  );
  const events = useMemo(
    () => [...eventsByFeed.values()].filter((event): event is DriverEvent => event !== null),
    [eventsByFeed],
  );

  const merged = useMemo(() => {
    const perFeed = new Map(volatile.map((row) => [row.feedId, row]));
    // With a team in force the roster already picked each driver's team channel; activity reads that channel alone.
    const teamFeedIds = filter.teamAliases.length > 0 ? new Set(roster.map((row) => row.feedId)) : null;
    const activity = activityByDriver(
      teamFeedIds ? volatile.filter((row) => teamFeedIds.has(row.feedId)) : volatile,
      teamFeedIds ? events.filter((event) => teamFeedIds.has(event.feedId)) : events,
    );
    const merged: DriverRow[] = roster.map((row) => {
      const feedVolatile = row.feedId != null ? perFeed.get(row.feedId) : undefined;
      const driverActivity = activity.get(row.workspaceMembershipId);
      return {
        ...row,
        recentMessage: driverActivity?.recentMessage ?? null,
        unread: driverActivity?.unread ?? null,
        latestItemCreatedAt: driverActivity?.latestItemCreatedAt ?? null,
        recentSource: driverActivity?.recentSource ?? null,
        location: feedVolatile?.location ?? null,
        volume: feedVolatile?.volume ?? null,
        country: feedVolatile?.country ?? null,
        stateProvince: feedVolatile?.stateProvince ?? null,
        calculatedAvailability: feedVolatile?.calculatedAvailability ?? null,
        offline: feedVolatile?.offline ?? null,
        mostRecentHandsFreeStatusEnabled: feedVolatile?.mostRecentHandsFreeStatusEnabled ?? null,
        mostRecentHandsFreeStatusUpdatedAt: feedVolatile?.mostRecentHandsFreeStatusUpdatedAt ?? null,
        mostRecentVolumeAudible: feedVolatile?.mostRecentVolumeAudible ?? null,
        mostRecentDrivingStatus: feedVolatile?.mostRecentDrivingStatus ?? null,
        mostRecentDutyStatusStatus: feedVolatile?.mostRecentDutyStatusStatus ?? null,
        mostRecentDutyStatusChangedAt: feedVolatile?.mostRecentDutyStatusChangedAt ?? null,
        mostRecentDrivingStatusChangedAt: feedVolatile?.mostRecentDrivingStatusChangedAt ?? null,
        membershipAvailabilityUpdatedAt: feedVolatile?.membershipAvailabilityUpdatedAt ?? null,
        latitude: feedVolatile?.latitude ?? null,
        longitude: feedVolatile?.longitude ?? null,
        locationUpdatedAt: feedVolatile?.locationUpdatedAt ?? null,
        locationSource: feedVolatile?.locationSource ?? null,
        locationPlaceName: feedVolatile?.locationPlaceName ?? null,
        tags: tagValueMap.get(row.workspaceMembershipId) ?? {},
        hasPersonalDevice: personalDeviceMembershipIds.has(row.workspaceMembershipId),
        route: routes.get(row.workspaceMembershipId) ?? null,
        neverLoggedIn: isNeverLoggedIn(
          unactivatedMembershipIds.has(row.workspaceMembershipId),
          feedVolatile?.mostRecentHandsFreeStatusUpdatedAt,
        ),
      };
    });
    return dedupeDriversByMembership(merged);
  }, [
    roster,
    volatile,
    events,
    tagValueMap,
    personalDeviceMembershipIds,
    unactivatedMembershipIds,
    routes,
    filter.teamAliases.length,
  ]);
  const rows = useStableRows(merged, byMembership);

  // Each row's latest driver message downloads its own text.
  const latestItemIdsKey = useMemo(
    () => latestItemIdsOf(rows, volatile, filter.teamAliases.length > 0).join(","),
    [rows, volatile, filter.teamAliases.length],
  );
  useMessageContentDownload(workspaceId, latestItemIdsKey, language, {
    what: "Driver message",
    holds: holdsTranscriptIn,
  });

  return { rows, total: rows.length };
}

/**
 * The latest driver message of each listed driver, the item whose text their row's recent message reads: the newest
 * across the driver's channels, or their row's own channel alone when a team is in force.
 */
function latestItemIdsOf(rows: readonly DriverRow[], volatile: readonly DriverVolatileRow[], teamInForce: boolean) {
  const listed = new Set(rows.map((row) => row.workspaceMembershipId));
  const rowFeedIds = teamInForce ? new Set(rows.map((row) => row.feedId)) : null;
  const newest = new Map<string, DriverVolatileRow>();
  for (const row of volatile) {
    // A feed with no driver message, or whose driver the hook does not list, has nothing to download.
    if (!row.aliasMembershipId || !row.latestItemId || !listed.has(row.aliasMembershipId)) continue;
    // With a team in force a row reads its own channel alone, as activityByDriver does.
    if (rowFeedIds && !rowFeedIds.has(row.feedId)) continue;
    // Across a driver's channels the newest message is the one the row shows.
    const existing = newest.get(row.aliasMembershipId);
    if (!existing || (row.latestItemCreatedAt ?? "") > (existing.latestItemCreatedAt ?? "")) {
      newest.set(row.aliasMembershipId, row);
    }
  }
  return [...newest.values()].flatMap((row) => (row.latestItemId ? [row.latestItemId] : []));
}

/**
 * Whether the store already holds a transcript of the item in `language` or a dialect of it, which the driver
 * transcripts warmup loads for every driver's messages; the original alone does not count.
 */
function holdsTranscriptIn(itemId: string, language: string): boolean {
  const transcription = transcriptionForContent(store.get("item", itemId)?.contentId, language);
  return transcription != null && transcription.language.split("-")[0] === language.split("-")[0];
}
